import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createContext } from 'react';

import Utils from '../utils/immersive_utils';

import {
  CredentialsRequest,
  DeleteCredentialsRequest,
  GetCredentialsRequest,
} from './model/credentials';
import { IAccountOption } from './portfolio-management';
import { WebSocketContext } from './websocket-context/websocket-provider';

type CreateAccount = {
  key: string;
  secret: string;
  name: string;
  passphrase?: string;
  testnet?: boolean;
};

type Accounts = {
  exchange: string;
  items: IAccountOption[];
};

type Venue = {
  value: string;
  name: string;
  children: {
    name: string;
    value: string;
  }[];
};

interface IVenueParamsProvider {
  children?: ReactNode;
}

interface IVenueProvider {
  credentials: Accounts[];
  venueList: Venue[];
  saveAccount: (account: CreateAccount, exchange: string) => void;
  deleteAccount: (name: string) => void;
  isLoading: boolean;
  isSuccess: boolean;
  setIsSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  hasErrors: boolean;
  errorDetail: string;
  refreshAccounts: () => void;
  filterVenueListForDeribitAccounts: Venue[];
}

const { naturalSort } = Utils;

const venueSort = (arr: Venue[]) => {
  return arr.sort((a, b) => (naturalSort([a.name, b.name])[0] === a.name ? -1 : 1));
};

const groupByExchange = (arr: { name: string; exchange: string; venue?: string }[]): Accounts[] => {
  const grouped: { [key: string]: IAccountOption[] } = {};
  arr?.forEach((item) => {
    if (!grouped[item.exchange]) {
      grouped[item.exchange] = [];
    }
    grouped[item.exchange].push({
      name: item.name,
      exchange: item.exchange,
      venue: item.venue || '',
    });
  });

  const result: Accounts[] = Object.keys(grouped).map((exchange) => ({
    exchange,
    items: grouped[exchange].sort((a, b) => (naturalSort([a.name, b.name])[0] === a.name ? -1 : 1)),
  }));

  return result;
};

const processOtcCredentials = (accounts: string[], venue: string) => {
  const otcAccounts = accounts.map((accountName) => ({
    name: accountName,
    exchange: 'otc',
    venue,
  }));

  return otcAccounts;
};

const convertCredentialsToVenueList = (credentials: Accounts[]) => {
  const list = credentials.map((account) => {
    const items: any = account.items.map((item: any) => ({
      name: item.name,
      value: item.name,
    }));

    return {
      value: account.exchange,
      name: account.exchange.charAt(0).toUpperCase() + account.exchange.slice(1),
      children: venueSort(items),
    };
  });

  list.sort((a, b) => a.name.localeCompare(b.name));

  return list;
};

const CREDENTIALS_GLOBAL_KEY = 'CREDENTIALS_GLOBAL_KEY';
const CREDENTIALS_OTC_KEY = 'CREDENTIALS_OTC_KEY';

// Pull a human-readable detail out of a JSON-RPC error ({ code, message, data }).
// Prefer the more specific `data` payload, falling back to `message`, so the
// credentials dialog can relay the backend's real reason instead of a fixed
// string. Returns '' when there is nothing to show.
const extractErrorDetail = (error: unknown): string => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  const err = error as { message?: unknown; data?: unknown };
  if (typeof err.data === 'string' && err.data) return err.data;
  const dataMessage = (err.data as { message?: unknown })?.message;
  if (typeof dataMessage === 'string' && dataMessage) return dataMessage;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '';
};

export const VenueContext = createContext({} as IVenueProvider);

export const VenueProvider = (props: IVenueParamsProvider) => {
  const { subscribeToChannel, sendMessage } = useContext(WebSocketContext);
  const [credentials, setCredentials] = useState<Accounts[]>([]);
  const [globalCredentials, setGlobalCredentials] = useState<Accounts[]>([]);
  const [otcCredentials, setOTCCredentials] = useState<Accounts[]>([]);

  const [isLoadingOTCCredentials, setIsLoadingOTCCredentials] = useState(false);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);

  const [isSuccess, setIsSuccess] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');

  const [isLoading, setIsloading] = useState(false);
  const [venueList, setVenueList] = useState<Venue[]>([]);

  useEffect(() => {
    setIsloading(isLoadingCredentials || isLoadingOTCCredentials);
  }, [isLoadingCredentials, isLoadingOTCCredentials]);

  const venueCredentialList = useMemo(
    () => convertCredentialsToVenueList(globalCredentials),
    [globalCredentials],
  );

  const otcVenueList = useMemo(
    () => convertCredentialsToVenueList(otcCredentials),
    [otcCredentials],
  );

  useEffect(() => {
    if (isLoading) return;

    setCredentials([...globalCredentials, ...otcCredentials]);
  }, [globalCredentials, otcCredentials, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    setVenueList([...venueCredentialList, ...otcVenueList]);
  }, [venueCredentialList, otcVenueList, isLoading, otcCredentials, globalCredentials]);

  const filterVenueListForDeribitAccounts = venueList.filter((venue) => venue.value === 'deribit');

  const handleResponse = (response: any) => {
    if (!response) return;

    if (response?.error) {
      setIsSuccess(false);
      setIsLoadingCredentials(false);
      setHasErrors(true);
      setErrorDetail(extractErrorDetail(response.error));
      return;
    }

    if (response?.result === 'OK') {
      setIsSuccess(true);
      setIsLoadingCredentials(false);
      setHasErrors(false);
      setErrorDetail('');
      return;
    }

    if (response?.id && typeof response?.result !== 'string') {
      if (response?.id === CREDENTIALS_GLOBAL_KEY) {
        const newCredentials = groupByExchange(response?.result);
        setGlobalCredentials(newCredentials);
        setIsLoadingCredentials(false);
      }
    }
  };

  const handleOtcCredentials = (response: any) => {
    if (!response) return;

    if (response?.error) {
      setIsLoadingOTCCredentials(false);
      setHasErrors(true);
      setErrorDetail(extractErrorDetail(response.error));
      return;
    }

    if (response?.id === CREDENTIALS_OTC_KEY) {
      const rawResult = response?.result || [];
      const allOtcAccounts = rawResult.flatMap((item: any) =>
        processOtcCredentials(item.accounts || [], item.venue || ''),
      );

      const groupedOtcCredentials = groupByExchange(allOtcAccounts);
      setOTCCredentials(groupedOtcCredentials);
      setIsLoadingOTCCredentials(false);
    }
  };

  const saveAccount = (account: CreateAccount, exchange: string) => {
    const params = new CredentialsRequest(
      CREDENTIALS_GLOBAL_KEY,
      account.name || account.key,
      exchange,
      account.key,
      account.secret,
      account.passphrase,
      account.testnet,
    );

    sendMessage(JSON.stringify(params));
    setIsLoadingCredentials(true);
    setIsSuccess(false);
    subscribeToChannel(CREDENTIALS_GLOBAL_KEY, handleResponse);
  };

  const deleteAccount = (name: string) => {
    const params = new DeleteCredentialsRequest(CREDENTIALS_GLOBAL_KEY, name);

    sendMessage(JSON.stringify(params));
    setIsLoadingCredentials(true);
    setIsSuccess(false);
  };

  const getCredentials = useCallback(() => {
    const params = new GetCredentialsRequest(CREDENTIALS_GLOBAL_KEY);
    setIsLoadingCredentials(true);
    sendMessage(JSON.stringify(params));
    subscribeToChannel(CREDENTIALS_GLOBAL_KEY, handleResponse);
  }, [sendMessage, subscribeToChannel]);

  const getOtcCredentials = useCallback(() => {
    const params = {
      jsonrpc: '2.0',
      id: CREDENTIALS_OTC_KEY,
      method: 'list_otc_venues',
      params: {},
    };

    sendMessage(JSON.stringify(params));
    subscribeToChannel(CREDENTIALS_OTC_KEY, handleOtcCredentials);
  }, [sendMessage, subscribeToChannel]);

  const refreshAccounts = useCallback(() => {
    getCredentials();
    getOtcCredentials();
  }, [getCredentials, getOtcCredentials]);

  useEffect(() => {
    getCredentials();
    getOtcCredentials();
  }, [getCredentials, getOtcCredentials]);

  return (
    <VenueContext.Provider
      value={{
        credentials,
        venueList,
        saveAccount,
        deleteAccount,
        isLoading,
        hasErrors,
        errorDetail,
        isSuccess,
        setIsSuccess,
        refreshAccounts,
        filterVenueListForDeribitAccounts,
      }}
    >
      {props.children}
    </VenueContext.Provider>
  );
};
